import React, {useEffect, useMemo, useState} from "react"
// MUI
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    Grid2 as Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from "@mui/material";
// Data
import {useData} from "@/context/PackagingDataProvider";
import {useUser} from "@/context/UserProvider";
import {usePermissions} from "@/context/PermissionsProvider";
// UI
import UserSelect from "@/components/UI/UserSelect/UserSelect";
import {FieldLabel} from "@/components/Layout";
import {packagingTokens as tk} from "@/theme/packagingTokens";

type OrgOption = {
    id: string,
    name: string,
    users: any[],
}

export default function BrandsEditor(props: any) {

    const {
        creating, editing,
        brand, setBrand,
        onCreate, onEdit,
        t
    } = props
    useData()
    const {fullOrganisation, getFullOrganisation} = useUser()
    const {isAccountRole} = usePermissions()
    const isMember = isAccountRole(["MEMBER"])

    // Track the original org so we can open a confirmation dialog on edit
    // when the org actually changes — the backend cascades the new owner to
    // every range and product under this brand, and a cross-org cascade is
    // the one users want to be asked about. Same-org user swaps don't need
    // the warning: visibility stays inside the same team.
    const [originalOrgUuid] = useState<string>(brand?.attributed_org_uuid || "")
    const [confirmCascadeOpen, setConfirmCascadeOpen] = useState<boolean>(false)

    // Local-only: the picked org scopes the user picker. Not sent to the
    // backend — the backend derives org from the attributed user's uuid.
    const [attributedOrgUuid, setAttributedOrgUuid] = useState<string>(brand?.attributed_org_uuid || "")

    useEffect(() => {
        if (!fullOrganisation) getFullOrganisation()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const orgOptions: OrgOption[] = useMemo(() => {
        if (!fullOrganisation) return []
        const opts: OrgOption[] = [{
            id: fullOrganisation.organisation_id,
            name: fullOrganisation.organisation_name,
            users: fullOrganisation.users ?? [],
        }]
        // MEMBERs can't reach this editor (write-locked below) but mirror the
        // sidebar org-selector rule (root org + sub-orgs for non-members) so
        // the option list stays consistent if the lock is ever relaxed.
        if (!isMember && Array.isArray(fullOrganisation.sub_organisations)) {
            fullOrganisation.sub_organisations.forEach((sub: any) => {
                opts.push({
                    id: sub.organisation_id,
                    name: sub.organisation_name,
                    users: sub.users ?? [],
                })
            })
        }
        return opts
    }, [fullOrganisation, isMember])

    const selectedOrg = orgOptions.find((o) => o.id === attributedOrgUuid)

    // Ghost-pad the users list so the attributed user always has a matching
    // MenuItem — even when they aren't in the selected org's users (moved
    // to another org, filtered out, or the attribution points to an org
    // not yet loaded). Without this the MUI Select renders empty and the
    // user only sees the floating "Attributed user" label.
    const usersForSelect = useMemo(() => {
        const base = (selectedOrg?.users ?? []) as any[]
        const uuid = brand?.attributed_user_uuid
        const email = brand?.attributed_user_email
        if (!uuid || !email) return base
        if (base.some((u: any) => u.user_id === uuid)) return base
        return [{user_id: uuid, email, role: ""}, ...base]
    }, [selectedOrg, brand?.attributed_user_uuid, brand?.attributed_user_email])

    const orgChanged =
        !isMember &&
        editing && originalOrgUuid !== "" &&
        attributedOrgUuid !== "" &&
        attributedOrgUuid !== originalOrgUuid

    const handlePrimaryAction = () => {
        if (creating) return onCreate()
        if (!editing) return
        if (orgChanged) {
            setConfirmCascadeOpen(true)
            return
        }
        onEdit()
    }

    const confirmCascadeAndEdit = () => {
        setConfirmCascadeOpen(false)
        onEdit()
    }

    const handleOrgChange = (newOrgId: string) => {
        if (isMember) return
        setAttributedOrgUuid(newOrgId)
        // Clear the attributed user — their uuid belonged to the previous org.
        setBrand({...brand, attributed_user_uuid: ""})
    }

    return (
        <>
            <Stack spacing={3}>
                <Box>
                    <FieldLabel label={t("brand_name")} required />
                    <TextField
                        error={brand.name === ""}
                        helperText={brand.name === "" ? t("brand_name_empty_error") : ""}
                        size="small"
                        variant="outlined"
                        fullWidth
                        value={brand.name}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setBrand({...brand, name: event.target.value})
                        }}/>
                </Box>
                <Box>
                    <FieldLabel label={t("brand_description")} description={t("brand_description_label")} />
                    <TextField
                        size="small"
                        variant="outlined"
                        value={brand.description}
                        multiline
                        rows={4}
                        fullWidth
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setBrand({...brand, description: event.target.value})
                        }}
                    />
                </Box>
                <Box>
                    <FieldLabel
                        label={t("attributed_to", "Attributed to")}
                        description={t("attributed_to_brand_label", "Pick a teammate to own this brand. Every range and product under the brand will follow.")}
                    />
                    <Grid container spacing={2}>
                        <Grid size={{xs: 12, md: 6}}>
                            <FormControl fullWidth size="small" disabled={isMember || orgOptions.length === 0}>
                                <InputLabel id="brand-attributed-org-label">
                                    {t("attributed_organisation", "Attributed organisation")}
                                </InputLabel>
                                <Select
                                    labelId="brand-attributed-org-label"
                                    label={t("attributed_organisation", "Attributed organisation")}
                                    value={attributedOrgUuid}
                                    onChange={(e) => handleOrgChange(String(e.target.value))}
                                >
                                    {orgOptions.map((o) => (
                                        <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{xs: 12, md: 6}}>
                            <UserSelect
                                label={t("attributed_user", "Attributed user")}
                                value={brand.attributed_user_uuid || ""}
                                users={usersForSelect}
                                disabled={isMember || !selectedOrg}
                                helperText={!selectedOrg ? t("select_organisation_first", "Select an organisation first") : undefined}
                                onChange={(userUuid) => {
                                    if (isMember) return
                                    setBrand({...brand, attributed_user_uuid: userUuid})
                                }}
                            />
                        </Grid>
                    </Grid>
                </Box>
                <Box sx={{display: "flex", justifyContent: "flex-end", pt: 1, borderTop: `1px solid ${tk.color.borderLight}`}}>
                    <Button
                        onClick={handlePrimaryAction}
                        disabled={brand.name === ""}
                        variant={"contained"}
                        disableElevation
                        sx={{textTransform: "none", fontWeight: 600, borderRadius: tk.radius.sm, px: 2.5}}
                    >
                        {creating ? t("brand_create") : editing ? t("brand_edit") : t("common:loading")}
                    </Button>
                </Box>
            </Stack>

            <Dialog open={confirmCascadeOpen} onClose={() => setConfirmCascadeOpen(false)}>
                <DialogTitle>{t("reassign_brand_title", "Reassign this brand?")}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t(
                            "reassign_brand_body",
                            "Changing the attributed user of this brand will also reassign every range and every product under it. Continue?",
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmCascadeOpen(false)}>
                        {t("common:cancel", "Cancel")}
                    </Button>
                    <Button onClick={confirmCascadeAndEdit} variant="contained" color="primary" disableElevation>
                        {t("common:confirm", "Confirm")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
